import { Node, mergeAttributes, Extension } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { changedDescendants } from '../utils.js';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const updateReplaceOldList = (node, schema, parentType) => {
  if (node.isInline || node.attrs["data-fbc-converted"]) {
    return node;
  }
  const orderedListType = schema.nodes.orderedList;
  const bulletListType = schema.nodes.bulletList;
  const taskListType = schema.nodes.taskList;
  const dropdownItemType = schema.nodes.dropdownItem;
  const dropdownItemTitleType = schema.nodes.dropdownItemTitle;
  const dropdownItemContentType = schema.nodes.dropdownItemContent;
  const fbcNumberListType = schema.nodes.fbcNumberList;
  const fbcBulletListType = schema.nodes.fbcBulletList;
  const fbcCheckListType = schema.nodes.fbcCheckList;
  const fbcToggleListType = schema.nodes.fbcToggleList;
  const listItemType = schema.nodes.listItem;
  const taskItemType = schema.nodes.taskItem;
  const listTypeNames = [
    orderedListType.name,
    bulletListType.name,
    taskListType.name
  ];
  if (!orderedListType || !fbcNumberListType || !fbcBulletListType || !bulletListType || !taskListType || !fbcCheckListType || !taskItemType || !listItemType || !dropdownItemType || !dropdownItemTitleType || !dropdownItemContentType || !fbcToggleListType) return node;
  if (listTypeNames.includes(node.type.name)) {
    const newNodes = [];
    node.content.forEach((item) => {
      const processedChild = updateReplaceOldList(item, schema, node.type);
      if (processedChild) {
        if (processedChild instanceof Fragment) {
          processedChild.forEach((n) => newNodes.push(n));
        } else {
          newNodes.push(processedChild);
        }
      }
    });
    return Fragment.fromArray(newNodes);
  }
  let newNode = node;
  if (node.type.name === listItemType.name && parentType) {
    newNode = parentType?.name === orderedListType.name ? fbcNumberListType.create(node.attrs) : fbcBulletListType.create(node.attrs);
  } else if (node.type.name === taskItemType.name && parentType) {
    newNode = parentType?.name === taskListType.name ? fbcCheckListType.create(node.attrs) : node;
  }
  if (node.type.name === dropdownItemType.name) {
    const titleNode = node.content.firstChild;
    const contentNode = node.childCount > 1 ? node.content.lastChild : null;
    if (titleNode && titleNode.type.name === dropdownItemTitleType.name && (!contentNode || contentNode.type.name === dropdownItemContentType.name)) {
      newNode = fbcToggleListType.create(node.attrs);
    } else {
      return null;
    }
  }
  if (node.childCount > 0) {
    let childrenChanged = false;
    const newChildNodes = [];
    node.content.forEach((item) => {
      const processedChild = updateReplaceOldList(item, schema, newNode.type);
      if (processedChild) {
        if (processedChild instanceof Fragment) {
          processedChild.forEach((n) => newChildNodes.push(n));
          childrenChanged = true;
        } else if (processedChild !== item) {
          newChildNodes.push(processedChild);
          childrenChanged = true;
        } else {
          newChildNodes.push(item);
        }
      } else {
        childrenChanged = true;
      }
    });
    if (node.type.name === dropdownItemTitleType.name || node.type.name === dropdownItemContentType.name) {
      return Fragment.fromArray(newChildNodes);
    }
    if (childrenChanged || newNode !== node) {
      try {
        return newNode.type.create(node.attrs, Fragment.fromArray(newChildNodes), node.marks);
      } catch {
        return newNode;
      }
    }
  } else if (node.type.name === dropdownItemTitleType.name || node.type.name === dropdownItemContentType.name) {
    return null;
  }
  return newNode;
};
const OrderedList = Node.create({
  priority: 1,
  name: "orderedList",
  content: "listItem+",
  group: "block list",
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "ol",
        getAttrs: () => false
      }
    ];
  },
  renderHTML() {
    return [
      "ol",
      {
        "data-deprecated": "true"
      },
      0
    ];
  },
  addAttributes() {
    return {
      start: {
        default: 1,
        parseHTML: (element) => {
          return element.hasAttribute("start") ? parseInt(element.getAttribute("start") || "", 10) : 1;
        }
      },
      type: {
        default: null,
        parseHTML: (element) => element.getAttribute("type")
      }
    };
  }
});
const BulletList = Node.create({
  priority: 1,
  name: "bulletList",
  content: "listItem+",
  group: "block list",
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  renderHTML() {
    return [
      "ul",
      {
        "data-deprecated": "true"
      },
      0
    ];
  }
});
const TaskList = Node.create({
  priority: 1,
  name: "taskList",
  content: "taskItem+",
  group: "block list",
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: 'ul[data-type="taskList"]',
        getAttrs: () => false
      }
    ];
  },
  renderHTML() {
    return [
      "ul",
      {
        "data-type": "taskList",
        "data-deprecated": "true"
      },
      0
    ];
  }
});
const ListItem = Node.create({
  priority: 1,
  name: "listItem",
  content: "paragraph block*",
  defining: true,
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "li",
        getAttrs: () => false
      }
    ];
  },
  renderHTML() {
    return [
      "li",
      {
        "data-deprecated": "true"
      },
      0
    ];
  }
});
const TaskItem = Node.create({
  priority: 1,
  name: "taskItem",
  content: "paragraph block*",
  defining: true,
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  addAttributes() {
    return {
      checked: {
        default: false,
        keepOnSplit: false,
        parseHTML: (element) => {
          const dataChecked = element.getAttribute("data-checked");
          return dataChecked === "" || dataChecked === "true";
        },
        renderHTML: (attributes) => ({
          "data-checked": attributes.checked
        })
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: 'li[data-type="taskItem"]',
        getAttrs: () => false
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "li",
      mergeAttributes(HTMLAttributes, {
        "data-type": "taskItem",
        "data-deprecated": "true"
      }),
      0
    ];
  }
});
const DropdownItem = Node.create({
  priority: 1,
  name: "dropdownItem",
  content: "dropdownItemTitle dropdownItemContent?",
  group: "block",
  defining: true,
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  addAttributes() {
    return {
      expanded: {
        default: true,
        parseHTML: (element) => element.getAttribute("data-expanded"),
        renderHTML: (attributes) => ({
          "data-expanded": attributes["data-expanded"]
        }),
        keepOnSplit: true
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: `div[data-node-type="dropdownItem"]`
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        ["data-node-type"]: "dropdownItem",
        ["data-deprecated"]: "true"
      }),
      0
    ];
  }
});
const DropdownItemTitle = Node.create({
  priority: 1,
  name: "dropdownItemTitle",
  content: "(paragraph | heading)",
  group: "dropdownItem",
  defining: true,
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: `div[data-node-type="dropdownItemTitle"]`
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-node-type": "dropdownItemTitle",
        "data-deprecated": "true"
      }),
      0
    ];
  }
});
const DropdownItemContent = Node.create({
  priority: 1,
  name: "dropdownItemContent",
  content: "block+",
  group: "dropdownItem",
  addStorage() {
    return {
      markdown: {
        parse: void 0
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: `div[data-node-type="dropdownItemContent"]`
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-node-type": "dropdownItemContent",
        "data-deprecated": "true"
      }),
      0
    ];
  }
});
const convertToFbc = (state, oldDocument) => {
  if (oldDocument) {
    changedDescendants(oldDocument, state.doc, 0, (node) => {
    });
  }
  const replacements = [];
  const transaction = state.tr;
  state.doc.forEach((node, pos) => {
    const replacedChild = updateReplaceOldList(node, state.schema, state.doc.type);
    if (replacedChild && replacedChild !== node) {
      replacements.push({
        type: "replacement",
        pos,
        original: node,
        replacement: replacedChild
      });
    }
  });
  const sortedReplacements = replacements.sort((a, b) => a.pos - b.pos);
  for (let i = sortedReplacements.length - 1; i >= 0; i--) {
    const { pos, original, replacement } = sortedReplacements[i];
    transaction.replaceWith(pos, pos + original.nodeSize, replacement);
  }
  if (transaction.docChanged) {
    transaction.setMeta("addToHistory", false);
    transaction.setMeta("$convertToFbc", true);
    return transaction;
  }
  return null;
};
const ListConverter = Extension.create({
  name: "listConverter",
  addGlobalAttributes() {
    return [
      {
        types: this.extensions.filter((ext) => ext.type === "node" && ext.name !== "text" && ext.name !== "doc").map((ext) => ext.name),
        attributes: {
          "compat-block-uuid": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          },
          "compat-block-created-at": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          }
        }
      },
      {
        types: [
          "fbcToggleList"
        ],
        attributes: {
          "compat-title-uuid": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          },
          "compat-title-created-at": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          }
        }
      },
      {
        types: [
          "fbcCheckList",
          "fbcNumberList",
          "fbcBulletList"
        ],
        attributes: {
          "compat-list-uuid": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          },
          "compat-list-created-at": {
            isRequired: false,
            keepOnSplit: false,
            rendered: false
          }
        }
      }
    ];
  },
  onCreate() {
    const view = this.editor.view;
    const { state } = view;
    if (!view || !state || !this.editor.isEditable) return;
    const tr = convertToFbc(state);
    if (tr) view.dispatch(tr);
  },
  addExtensions() {
    return [
      OrderedList,
      BulletList,
      ListItem,
      TaskList,
      TaskItem,
      DropdownItemTitle,
      DropdownItemContent,
      DropdownItem
    ];
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("listConverter"),
        appendTransaction: (transactions, oldState, newState) => {
          if (transactions.some((t) => t.getMeta("$convertToFbc"))) return null;
          const tr = convertToFbc(newState, oldState.doc);
          if (tr) {
            return tr;
          }
          return null;
        }
      })
    ];
  }
});

export { ListConverter as default };
//# sourceMappingURL=ListConverter.js.map
